'use client';

import { useEffect, type RefObject } from 'react';

const DURATION_MS = 1400;
const STAGGER_MS = 90;

// Fast start, long settle: reads as "counting up" without a mechanical tick.
const easeOutExpo = (t: number) => (t >= 1 ? 1 : 1 - 2 ** (-10 * t));

/**
 * Counts every `[data-count-to]` element inside `rootRef` from 0 to its target
 * the first time the root is 30% in view, staggered left to right.
 *
 * SSR and no-JS render the final numbers; the hook only zeroes them once it is
 * about to animate, and never does under prefers-reduced-motion. Text is
 * written straight to the DOM so the strip does not re-render per frame.
 */
export const useCountUpOnView = (rootRef: RefObject<HTMLElement | null>) => {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const targets = Array.from(
      root.querySelectorAll<HTMLElement>('[data-count-to]'),
    ).map((el) => ({
      el,
      to: Number(el.dataset.countTo),
      final: el.textContent ?? '',
    }));
    if (!targets.length) return;

    const format = (n: number, final: string) => {
      if (final.includes('.')) {
        const decimals = final.split('.')[1]?.length ?? 1;
        return n.toFixed(decimals);
      }
      return Math.round(n).toLocaleString('en-US');
    };

    let frame = 0;
    const run = () => {
      targets.forEach(({ el, final }) => {
        el.textContent = format(0, final);
      });
      const start = performance.now();
      const tick = (now: number) => {
        let done = true;
        targets.forEach(({ el, to, final }, i) => {
          const t = Math.max(0, now - start - i * STAGGER_MS) / DURATION_MS;
          if (t < 1) done = false;
          el.textContent = t >= 1 ? final : format(to * easeOutExpo(t), final);
        });
        if (!done) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        observer.disconnect();
        run();
      },
      { threshold: 0.3 },
    );
    observer.observe(root);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      // Leave the real numbers in place if we unmount mid-count.
      targets.forEach(({ el, final }) => {
        el.textContent = final;
      });
    };
  }, [rootRef]);
};
